import { toast } from 'sonner';
import type { Invoice } from '../types';

/**
 * Replicates the legacy V1 `atekPrint` iframe-based PDF print mechanism.
 * Creates a hidden off-screen iframe, writes the invoice HTML into it
 * (re-executing any <script> tags to match legacy behaviour), sets the
 * document title to the desired filename so the browser's "Save as PDF"
 * dialog uses it, waits 1 000 ms for images to load, then calls print.
 * Always restores the original document title and removes the iframe.
 */

/** Re-execute <script> tags inside an element (mirrors legacy setInnerHTML). */
function setInnerHTML(elm: HTMLElement, html: string): void {
  elm.innerHTML = html;

  Array.from(elm.querySelectorAll('script')).forEach((oldScript) => {
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    const text = document.createTextNode(oldScript.innerHTML);
    newScript.appendChild(text);
    oldScript.parentNode!.replaceChild(newScript, oldScript);
  });
}

/**
 * Build the filename for a printed invoice PDF.
 * Format: `ATEK Invoice {invoice_number}-{invoice_version} {project_name}`
 * Filesystem-illegal characters are stripped; whitespace is collapsed.
 * The browser appends ".pdf" when the user saves from the print dialog.
 */
export function buildInvoiceFileName(
  invoice: Pick<Invoice, 'invoice_number' | 'invoice_version' | 'project_name'>,
): string {
  const version = invoice.invoice_version ?? 1;
  const base = `ATEK Invoice ${invoice.invoice_number}-${version}${invoice.project_name ? ' ' + invoice.project_name : ''}`;
  // Strip filesystem-illegal characters, then collapse whitespace
  return base
    .replace(/[/\\:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Fetch the invoice HTML from the backend and trigger a browser print dialog
 * that uses `fileName` as the suggested PDF filename.
 */
export async function printInvoicePdf(
  invoiceId: string,
  fileName: string,
): Promise<void> {
  let frame: HTMLIFrameElement | null = null;
  const previousTitle = window.document.title;

  try {
    const response = await fetch(`/api/v3/invoices/${invoiceId}/html`, {
      credentials: 'include',
    });

    if (!response.ok) {
      toast.error('Failed to load invoice PDF');
      return;
    }

    const html = await response.text();

    frame = document.createElement('iframe');
    frame.style.position = 'absolute';
    frame.style.top = '-10000px';
    document.body.appendChild(frame);

    window.document.title = fileName;
    setInnerHTML(frame.contentDocument!.body, html);

    await new Promise<void>((resolve) => {
      setTimeout(() => {
        frame!.focus();
        frame!.contentWindow!.print();
        window.focus();
        resolve();
      }, 1000);
    });
  } catch {
    toast.error('Failed to load invoice PDF');
  } finally {
    window.document.title = previousTitle;
    if (frame && frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  }
}
